import React, { useState } from "react"
import styled from "styled-components"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { useLocation } from "react-router"
import { LinkContainer } from "react-router-bootstrap"
import { Nav } from "react-bootstrap"
import { useLanguageState } from "../../globalStates/LanguageState"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { hasServiceKeys } from "../../globalStates/ConfigDataState"
import WarningIcon from "../../images/warning.svg"
import Languages from "./Languages"
import { SidebarSection } from "../../branding/branding"
import { categoriesPageRoute, couponsPageRoute, eventDatesPageRoute, homePageRoute, jobsPageRoute, licencesPageRoute, linksPageRoute, multimediaPageRoute, newsPageRoute, printPageRoute, productsPageRoute, profilePageRoute, showroomsPageRoute, teamPageRoute, trademarksPageRoute } from "../../utils/RoutePaths"

interface SidebarWrapperProps {
    color: string
}

const SidebarWrapper = styled.nav<SidebarWrapperProps>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    z-index: 2;
    width: 270px;
    float: left;
    height: 100vh;
    box-shadow: 0px -10px 4px rgba(0, 0, 0, 0.25);

    & > .top > .nav {
        padding: 20px 10px 10px 10px;
        .nav-item {
            display: flex;

            img {
                height: 25px;
                width: 25px;
                margin-top: 10px;
            }
        }
        .nav-link {
            padding: 15px;
            color: #727272;
            font-weight: 500;
        }
        .nav-link.active {
            color: ${(props) => props.color || "#6DD400"};
        }
    }
`

const TopWrapper = styled.div``

const BottomWrapper = styled.div`
    padding: 25px;
`

const Logo = styled.div`
    height: 80px;
    padding: 10px 25px;
    display: flex;
    align-items: center;
`

interface LogoIconProps {
    source?: string
}

const LogoIcon = styled.div<LogoIconProps>`
    height: 60px;
    width: 150px;
    display: flex;
    align-items: center;
    background-image: url(${(props) => (!props.source ? "none" : props.source)});
    background-repeat: no-repeat;
    background-position-y: center;
    background-size: contain;
`

interface SidebarItemProps {
    id?: string
    label: string
    path: string
    itemState: State<any>
    depth?: number
    search: any
    style?: React.CSSProperties | ((isActive: boolean) => React.CSSProperties) | undefined
}

const SidebarItem: React.FC<SidebarItemProps> = ({ id, label, path, itemState, depth, search, style }) => {
    const scopedItemState = useHookstate(itemState)
    const errors = Validation(scopedItemState).errors(undefined, depth)
    // console.log(Validation(scopedItemState).errors())

    return (
        <Nav.Item id={id}>
            <LinkContainer style={style} to={{ pathname: path, search }}>
                <Nav.Link href={path}>{label}</Nav.Link>
            </LinkContainer>
            {errors.length !== 0 && <img src={WarningIcon} alt="warning" />}
        </Nav.Item>
    )
}

interface SidebarItemLinkProps {
    label: string
    path: string
}

const SidebarItemLink: React.FC<SidebarItemLinkProps> = ({ label, path }) => (
    <Nav.Item>
        <Nav.Link href={path} target="_blank">
            {label}
        </Nav.Link>
    </Nav.Item>
)

const Sidebar: React.FC<{}> = () => {
    const location = useLocation()
    const branding = useLanguageState().getStrings()
    const [activePage, setActivePage] = useState(location.pathname)

    const sectionsOrder = branding.configuration.sidebar.sectionsOrder
    const exiDataState = useExhibitorDataState()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const categoryVisible = hasServiceKeys(serviceKeys.category)
    // const licencesVisible = hasServiceKeys(serviceKeys.licence)
    const licencesPageVisible = branding.configuration.licencesPage.licencesPageVisible
    const linkVisible = hasServiceKeys(serviceKeys.link)
    const productsVisible = hasServiceKeys(serviceKeys.products)
    const trademarksVisible = hasServiceKeys(serviceKeys.trademarks)
    const teamsVisible = hasServiceKeys(serviceKeys.contactPartner)
    const multimediaVisible = hasServiceKeys(serviceKeys.multimedia)
    const newsVisible = hasServiceKeys(serviceKeys.news)
    const showroomsVisible =
        (serviceKeys.showroomPublic !== undefined && hasServiceKeys(serviceKeys.showroomPublic)) ||
        (serviceKeys.showroomPrivate !== undefined && hasServiceKeys(serviceKeys.showroomPrivate))
    const couponsVisible = hasServiceKeys(serviceKeys.goodies)
    const jobsVisible = hasServiceKeys(serviceKeys.jobAd)
    const printVisible =
        hasServiceKeys(serviceKeys.printOrganizationName) ||
        hasServiceKeys(serviceKeys.printAddress) ||
        hasServiceKeys(serviceKeys.printAddressCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printContact) ||
        hasServiceKeys(serviceKeys.printContactCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printLogo) ||
        hasServiceKeys(serviceKeys.printLogoExhibitorDirectory) ||
        hasServiceKeys(serviceKeys.printLogoCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printDescription) ||
        hasServiceKeys(serviceKeys.printAd)

    const eventDatesVisible =
        exiDataState.getEventDates() &&
        exiDataState.getEventDates().length > 0 &&
        (hasServiceKeys(serviceKeys.eventDate) ||
            hasServiceKeys(serviceKeys.eventDateMasterClass) ||
            hasServiceKeys(serviceKeys.eventDateRemoteLiveStream) ||
            hasServiceKeys(serviceKeys.eventDatePreRecorded) ||
            hasServiceKeys(serviceKeys.eventDateRoundTable))

    const getItemColor = (activePage: string) => {
        const path = activePage.substring(0, 8)
        const defaultColor = { color: "#727272" }
        const activePageColor = { color: branding.configuration.maincolor }
        return path === homePageRoute || path === profilePageRoute ? activePageColor : defaultColor
    }

    return (
        <SidebarWrapper color={branding.configuration.maincolor}>
            <TopWrapper className="top">
                <Logo>
                    <LogoIcon source="/branding/logo.png" />
                </Logo>
                <Nav activeKey={activePage} className="flex-column" onSelect={(selectedKey: any) => setActivePage(selectedKey)}>
                    {sectionsOrder.map((section: SidebarSection, index: number) => {
                        switch (section) {
                            case SidebarSection.PROFILE: {
                                return (
                                    <React.Fragment key={index}>
                                        <SidebarItem
                                            style={getItemColor(activePage)}
                                            label={branding.configuration.sidebar.lblProfile}
                                            path={profilePageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getOrganization()}
                                            depth={2}
                                        />
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.CATEGORY: {
                                return (
                                    <React.Fragment key={index}>
                                        {categoryVisible && (
                                            <SidebarItem
                                                id="categories"
                                                label={branding.configuration.sidebar.lblCategories}
                                                path={categoriesPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getCategories()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.LICENCES: {
                                return (
                                    <React.Fragment key={index}>
                                        {categoryVisible && licencesPageVisible && (
                                            <SidebarItem
                                                id="licences"
                                                label={branding.configuration.sidebar.lblLicences}
                                                path={licencesPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getCategories()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.LINKS: {
                                return (
                                    <React.Fragment key={index}>
                                        {linkVisible && (
                                            <SidebarItem
                                                id="links"
                                                label={branding.configuration.sidebar.lblLinks}
                                                path={linksPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getLinks()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.PRODUCTS: {
                                return (
                                    <React.Fragment key={index}>
                                        {productsVisible && (
                                            <SidebarItem
                                                id="products"
                                                label={branding.configuration.sidebar.lblProducts}
                                                path={productsPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getProducts()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.TRADEMARKS: {
                                return (
                                    <React.Fragment key={index}>
                                        {trademarksVisible && (
                                            <SidebarItem
                                                id="trademarks"
                                                label={branding.configuration.sidebar.lblTrademarks}
                                                path={trademarksPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getTrademarks()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.TEAM: {
                                return (
                                    <React.Fragment key={index}>
                                        {" "}
                                        {teamsVisible && (
                                            <SidebarItem
                                                id="team"
                                                label={branding.configuration.sidebar.lblTeam}
                                                path={teamPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getPersons()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.MULTIMEDIA: {
                                return (
                                    <React.Fragment key={index}>
                                        {multimediaVisible && (
                                            <SidebarItem
                                                id="multimedia"
                                                label={branding.configuration.sidebar.lblMultimedia}
                                                path={multimediaPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getMedias()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.NEWS: {
                                return (
                                    <React.Fragment key={index}>
                                        {newsVisible && (
                                            <SidebarItem
                                                id="news"
                                                label={branding.configuration.sidebar.lblNews}
                                                path={newsPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getNews()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.EVENTDATES: {
                                return (
                                    <React.Fragment key={index}>
                                        {eventDatesVisible && (
                                            <SidebarItem
                                                id="eventDates"
                                                label={branding.configuration.sidebar.lblEventDate}
                                                path={eventDatesPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getEventDates()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.SHOWROOMS: {
                                return (
                                    <React.Fragment key={index}>
                                        {showroomsVisible && (
                                            <SidebarItem
                                                id="showrooms"
                                                label={branding.configuration.sidebar.lblShowrooms}
                                                path={showroomsPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getShowrooms()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.COUPONS: {
                                return (
                                    <React.Fragment key={index}>
                                        {couponsVisible && (
                                            <SidebarItem
                                                id="coupons"
                                                label={branding.configuration.sidebar.lblCoupons}
                                                path={couponsPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getCoupons()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.JOBS: {
                                return (
                                    <React.Fragment key={index}>
                                        {jobsVisible && (
                                            <SidebarItem
                                                id="jobs"
                                                label={branding.configuration.sidebar.lblJobs}
                                                path={jobsPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getJobs()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.PRINT: {
                                return (
                                    <React.Fragment key={index}>
                                        {printVisible && (
                                            <SidebarItem
                                                id="print"
                                                label={branding.configuration.sidebar.lblPrint}
                                                path={printPageRoute}
                                                search={location.search}
                                                itemState={exiDataState.getPrintCatalog()}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            }

                            case SidebarSection.SHOP: {
                                return (
                                    <React.Fragment key={index}>
                                        <br />
                                        <br />
                                        <br />
                                        <br />
                                        <SidebarItemLink
                                            label={branding.configuration.sidebar.lblEventShop}
                                            path={branding.configuration.eventShopPage.eventShopUrl}
                                        />
                                    </React.Fragment>
                                )
                            }

                            default:
                                return null
                        }
                    })}
                </Nav>
            </TopWrapper>
            <BottomWrapper className="bottom">
                <Languages />
            </BottomWrapper>
        </SidebarWrapper>
    )
}

export default Sidebar
